<template>
  <a
    ref="buttonRef"
    href="#top"
    class="btn-scroll-top fixed bottom-6 right-6 z-50 flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium shadow-lg border border-gray-200 opacity-0 invisible transition-all duration-300 hover:bg-gray-50"
    :class="{ 'show': isVisible }"
    @click.prevent="scrollToTop"
  >
    <span>Top</span>
    <UIcon 
      name="i-heroicons-arrow-right" 
      class="w-4 h-4 -rotate-90 transition-transform duration-300"
      :class="{ 'translate-x-1': isVisible }"
    />
    <span class="absolute inset-0 rounded-full border border-gray-200"></span>
    <svg
      class="absolute inset-0 w-full h-full  z-10 pointer-events-none"
      viewBox="0 0 62 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        ref="progressRef"
        x="0.75"
        y="0.75"
        width="60.5"
        height="30.5"
        rx="15.25"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-miterlimit="10"
        class="text-primary-500"
        :style="progressStyle"
      />
    </svg>
  </a>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'

interface Props {
  scrollOffset?: number
}

const props = withDefaults(defineProps<Props>(), {
  scrollOffset: 600
})

const buttonRef = ref<HTMLAnchorElement | null>(null)
const progressRef = ref<SVGRectElement | null>(null)
const isVisible = ref(false)
const scrollPercent = ref(0)

const progressStyle = computed(() => {
  if (!progressRef.value) return {}
  
  const length = pathLength.value
  const draw = length * scrollPercent.value
  
  return {
    strokeDasharray: `${length}`,
    strokeDashoffset: `${length - draw}`
  }
})

const pathLength = ref(0)

const showProgress = () => {
  const scrollPosition = window.pageYOffset
  const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight
  scrollPercent.value = scrollPosition / scrollHeight || 0
}

const handleScroll = () => {
  if (window.pageYOffset > props.scrollOffset) {
    isVisible.value = true
  } else {
    isVisible.value = false
  }
  showProgress()
}

const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

onMounted(() => {
  if (progressRef.value) {
    pathLength.value = progressRef.value.getTotalLength()
  }
  
  window.addEventListener('scroll', handleScroll)
  handleScroll() // Initial check
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.btn-scroll-top {
  transform: translateY(100px);
}

.btn-scroll-top.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

/* Smooth animation for the icon */
.btn-scroll-top:hover .transition-transform {
  transform: translateX(0.25rem);
}
</style>
